/**
 * NativeLoginScreen for Poker Reborn
 *
 * Native implementation: social buttons are icon-only PixiJS Buttons (48x48)
 * in a horizontal row. Guest button is full-width below.
 * Google/Facebook/Apple use Capacitor SocialLogin plugin via AuthService.
 *
 * @see docs/screens/login/NATIVE.md - Native-specific specs
 * @see docs/screens/login/README.md - General login flow
 */

import { Assets, Sprite, Texture } from 'pixi.js';
import { LoginScreenBase } from './LoginScreenBase';
import { Button, GOLD_BUTTON_STYLE } from '@/components/common/Button';
import { authService } from '@/services/AuthService';
import type { AuthProvider } from '@/services/AuthService';
import { log } from '@/utils/log';

// =============================================================================
// SOCIAL ICON PATHS
// =============================================================================

const SOCIAL_ICONS = {
  google: 'assets/images/social/google.png',
  facebook: 'assets/images/social/facebook.png',
  apple: 'assets/images/social/apple.png',
} as const;

// =============================================================================
// NATIVE LOGIN SCREEN
// =============================================================================

export class NativeLoginScreen extends LoginScreenBase {
  private googleButton: Button | null = null;
  private facebookButton: Button | null = null;
  private appleButton: Button | null = null;
  private guestButton: Button | null = null;

  // ===========================================================================
  // ABSTRACT IMPLEMENTATIONS
  // ===========================================================================

  protected mountButtons(): void {
    // Google button (icon-only, blue)
    this.googleButton = new Button({
      label: '',
      width: 48,
      height: 48,
      style: {
        backgroundColor: 0x4285f4,
        hoverColor: 0x5a95f5,
        pressColor: 0x3367d6,
        cornerRadius: 8,
      },
    });
    this.googleButton.onClick = () => { void this.handleSocialLogin('google'); };
    this.addChild(this.googleButton);

    // Facebook button (icon-only, Facebook blue)
    this.facebookButton = new Button({
      label: '',
      width: 48,
      height: 48,
      style: {
        backgroundColor: 0x1877f2,
        hoverColor: 0x1565d8,
        pressColor: 0x0d5bbd,
        cornerRadius: 8,
      },
    });
    this.facebookButton.onClick = () => { void this.handleSocialLogin('facebook'); };
    this.addChild(this.facebookButton);

    // Apple button (icon-only, black)
    this.appleButton = new Button({
      label: '',
      width: 48,
      height: 48,
      style: {
        backgroundColor: 0x000000,
        hoverColor: 0x222222,
        pressColor: 0x333333,
        cornerRadius: 8,
      },
    });
    this.appleButton.onClick = () => { void this.handleSocialLogin('apple'); };
    this.addChild(this.appleButton);

    // Load social icon textures and add as sprites
    void this.loadSocialIcons();

    // Guest button (full-width, gold)
    this.guestButton = new Button({
      label: 'Play as Guest',
      width: 280,
      height: 48,
      style: GOLD_BUTTON_STYLE,
    });
    this.guestButton.onClick = () => { void this.handleGuestLogin(); };
    this.addChild(this.guestButton);

    this.layoutButtons();
  }

  protected cleanupImpl(): void {
    if (this.googleButton) {
      this.googleButton.destroy();
      this.googleButton = null;
    }
    if (this.facebookButton) {
      this.facebookButton.destroy();
      this.facebookButton = null;
    }
    if (this.appleButton) {
      this.appleButton.destroy();
      this.appleButton = null;
    }
    if (this.guestButton) {
      this.guestButton.destroy();
      this.guestButton = null;
    }
  }

  protected onResize(): void {
    this.layoutButtons();
  }

  // ===========================================================================
  // PRIVATE
  // ===========================================================================

  private layoutButtons(): void {
    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;

    // Social icon row: 3 × 48px + 2 × 16px gaps = 176px total
    const rowWidth = 176;
    const rowStartX = centerX - rowWidth / 2;
    const socialY = centerY - 24;

    if (this.googleButton) {
      this.googleButton.x = rowStartX;
      this.googleButton.y = socialY;
    }
    if (this.facebookButton) {
      this.facebookButton.x = rowStartX + 64; // 48 + 16 gap
      this.facebookButton.y = socialY;
    }
    if (this.appleButton) {
      this.appleButton.x = rowStartX + 128; // 48 + 16 + 48 + 16
      this.appleButton.y = socialY;
    }
    if (this.guestButton) {
      this.guestButton.x = centerX - 140; // 280 / 2
      this.guestButton.y = centerY + 60;
    }
  }

  /** Load SVG icons and add as sprites centered on each social button */
  private async loadSocialIcons(): Promise<void> {
    const iconSize = 24;
    const buttonSize = 48;
    const offset = (buttonSize - iconSize) / 2;

    const entries: Array<[Button | null, string]> = [
      [this.googleButton, SOCIAL_ICONS.google],
      [this.facebookButton, SOCIAL_ICONS.facebook],
      [this.appleButton, SOCIAL_ICONS.apple],
    ];

    for (const [button, path] of entries) {
      if (!button) continue;
      try {
        const texture = await Assets.load<Texture>(path);
        const sprite = new Sprite(texture);
        sprite.width = iconSize;
        sprite.height = iconSize;
        sprite.x = offset;
        sprite.y = offset;
        sprite.eventMode = 'none';
        button.addChild(sprite);
      } catch {
        log.app.warning`Failed to load social icon: ${path}`;
      }
    }
  }

  /** Unified social login handler — delegates to AuthService */
  private async handleSocialLogin(provider: Exclude<AuthProvider, 'guest'>): Promise<void> {
    const providerLabel = provider.charAt(0).toUpperCase() + provider.slice(1);
    this.setStatus(`Signing in with ${providerLabel}...`);
    this.hideError();

    const buttonMap: Record<string, Button | null> = {
      google: this.googleButton,
      facebook: this.facebookButton,
      apple: this.appleButton,
    };
    const button = buttonMap[provider] ?? null;
    if (button) {
      button.disabled = true;
    }

    try {
      const loginMethod =
        provider === 'google' ? authService.googleLogin() :
        provider === 'facebook' ? authService.facebookLogin() :
        authService.appleLogin();

      const credentials = await loginMethod;

      // Cleanup and notify
      this.cleanup();
      this.onCredentialsReceived?.(credentials.username, credentials.password);
    } catch (error) {
      if (button) {
        button.disabled = false;
      }

      const errorMessage = error instanceof Error ? error.message : `${providerLabel} sign-in failed`;
      this.setStatus('');
      this.showError(errorMessage);
      log.auth.error`${providerLabel} login failed: ${errorMessage}`;
    }
  }
}
